/**
 * sl-pbux.3.1: Section container — vertical Flex-Column layout with optional
 * background + padding. Accepts any child (containers + leaves). 1-Level
 * nesting policy is enforced by the drop-policy table, not by render code.
 *
 * The actual child-placement happens via the DocPageSnapshot tree (public)
 * or the Builder Tree-Canvas (sl-pbux.3.3). This render produces the
 * wrapper element + a `data-sl-children-slot` attribute that the renderers
 * read to inject children.
 */
import { createElement, type ReactNode } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type { ViewComponent } from '../../../types/view-component.types';

const configSchema = z.object({
  background: z
    .enum(['transparent', 'subtle', 'tinted'])
    .default('transparent'),
  padding: z.enum(['none', 'sm', 'md', 'lg']).default('md'),
  gap: z.enum(['none', 'sm', 'md', 'lg']).default('md'),
});

const PADDING_PX: Record<z.infer<typeof configSchema>['padding'], string> = {
  none: '0',
  sm: '8px',
  md: '16px',
  lg: '24px',
};

const GAP_PX: Record<z.infer<typeof configSchema>['gap'], string> = {
  none: '0',
  sm: '8px',
  md: '16px',
  lg: '24px',
};

const BACKGROUND_TOKEN: Record<
  z.infer<typeof configSchema>['background'],
  string
> = {
  transparent: 'transparent',
  subtle: 'var(--sl-app-bg-subtle, rgba(0,0,0,0.02))',
  tinted: 'var(--sl-app-bg-tinted, rgba(0,0,0,0.04))',
};

export const SectionComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.SECTION,
  type: 'container',
  name: 'Section',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-app-font-size-sm)' } },
    '▭'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    background: 'transparent',
    padding: 'md',
    gap: 'md',
  },
  render({ config, mode }): ReactNode {
    const cfg = config as z.infer<typeof configSchema>;
    return createElement('div', {
      'data-sl-children-slot': 'children',
      'data-sl-mode': mode,
      style: {
        display: 'flex',
        flexDirection: 'column',
        gap: GAP_PX[cfg.gap],
        padding: PADDING_PX[cfg.padding],
        background: BACKGROUND_TOKEN[cfg.background],
        borderRadius: '6px',
      },
    });
  },
  palettePreview() {
    return createElement(
      'div',
      {
        style: {
          padding: 'var(--sl-app-padding-sm)',
          fontSize: 'var(--sl-app-font-size-xs)',
          color: 'var(--streamlain-v2-text-secondary)',
        },
      },
      '▭ Section'
    );
  },
};
